import type { VerifyAppleIAPResponse } from '@/api/pay'
import type { NativeIapRecoveryBridge } from '@/utils/platform'
import type { PendingApplePaymentContext } from '@/utils/paymentContext'

type RecoveryDependencies = {
  bridge?: NativeIapRecoveryBridge
  pendingContext: PendingApplePaymentContext | null
  verify: (input: {
    receipt_data: string
    order_id: string
  }) => Promise<VerifyAppleIAPResponse>
  recover: (receiptData: string, orderId?: string | null) => Promise<VerifyAppleIAPResponse>
  onConfirmed?: (orderId: string | null) => void
  onTransactionError?: (error: unknown) => void
}

export async function recoverUnfinishedAppleTransactions({
  bridge,
  pendingContext,
  verify,
  recover,
  onConfirmed,
  onTransactionError,
}: RecoveryDependencies): Promise<boolean> {
  if (!bridge) return false

  let recoveredTransaction = false
  const unfinished = await bridge.getUnfinishedTransactions()
  for (const transaction of unfinished.transactions) {
    if (
      pendingContext
      && transaction.app_account_token !== pendingContext.appAccountToken
    ) {
      continue
    }

    try {
      let confirmation: VerifyAppleIAPResponse
      const matchesPendingContext = Boolean(
        pendingContext
        && transaction.product_id === pendingContext.productId
        && transaction.app_account_token === pendingContext.appAccountToken
        && (
          !pendingContext.transactionId
          || pendingContext.transactionId === transaction.transaction_id
        ),
      )
      if (matchesPendingContext && pendingContext) {
        try {
          confirmation = await verify({
            receipt_data: transaction.receipt_data,
            order_id: pendingContext.orderId,
          })
        } catch {
          confirmation = await recover(transaction.receipt_data, pendingContext.orderId)
        }
      } else {
        confirmation = await recover(transaction.receipt_data)
      }

      if (confirmation.confirmed) {
        await bridge.finishTransaction(transaction.transaction_id)
        onConfirmed?.(matchesPendingContext && pendingContext ? pendingContext.orderId : null)
        recoveredTransaction = true
      }
    } catch (error) {
      onTransactionError?.(error)
    }
  }

  return recoveredTransaction
}
